<script>
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import DocumentAdd from './DocumentAdd.vue'
import { createDocument } from '../../Utils/document'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import { useDocumentTypeUnicity } from '../../Composables/useDocumentTypeUnicity.js'

export default {
  components: {
    ModalConfirmation,
    DocumentAdd,
    ButtonAll,
    SimpleGroupSelect,
    ModalDialog,
  },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    disabled: { type: Boolean, default: false },
    id: { type: String, required: true },
    context: { type: String, required: true },
    contextId: { type: Number, required: true },
    internalname: { type: String, required: false, default: 'none' },
  },

  emits: ['document-uploading', 'document-uploaded', 'update:document'],

  data: function () {
    return {
      document: createDocument({}, this.$root),
      documentGenerate: createDocument({}, this.$root),
      documentTypeDef: null,
      documentTypeGenerate: { options: { choices: [] } },
      commentDef: {},
      documentToSend: {},
      url: '',
      comment: '',
      loading: false,
      modal: null,
      generating: false,
    }
  },

  computed: {
    hasDocumentAdd() {
      return this.hasRole('ROLE_DOCUMENT_ADD') || (user && !user.isBusinessMan && this.context === 'Simulation')
    },
    hasDocumentGenerate() {
      return this.hasRole('ROLE_DOCUMENT_GENERATE') && this.internalname === 'none'
    },
    hasDocumentTypes() {
      return this.documentTypes.length > 0
    },
    hasDocumentTypeGenerates() {
      return Object.values(this.documentTypeGenerate?.options?.choices ?? []).length > 0
    },
    documentTypes() {
      return Object.values(this.documentTypeDef?.options?.choices ?? [])
    },
  },

  methods: {
    openModal(generate) {
      if (generate) {
        this.mode = 'generate'
        this.$refs[`${this.id}-modal-generate-document`].open()
      } else {
        this.mode = 'upload'
        this.comment = ''
        this.$refs[`${this.id}-modal-add-document`].open()

        this.documentToSend = {}
        this.loading = false
      }
    },
    closeModal(generate = false) {
      if (generate) {
        this.$refs[`${this.id}-modal-generate-document`].close(false)
      } else {
        this.$refs[`${this.id}-modal-add-document`].close(false)
        this.$refs.documentAdd.removeDocuments()
      }
    },
    async generateTemplate() {
      if (this.generating) {
        return
      }

      this.generating = true
      let documentToSend = {
        context: `AppBundle\\Entity\\${this.context}`,
        contextId: this.contextId,
        typeId: this.documentGenerate?.id,
      }

      try {
        await this.postJSON('documents', documentToSend)
        this.toast(this.$t('document.generatedOk'))
        this.closeModal(true)
        this.$emit('update:document')
      } finally {
        this.generating = false
      }
    },
    downloadDocument(e) {
      let documentToSend = {}
      documentToSend.typeId = this.internalname
      documentToSend.context = 'AppBundle\\Entity\\' + this.context
      documentToSend.contextId = this.contextId
      this.postJSON('documents', documentToSend).then(async (json) => {
        let downloadName
        if (json) {
          downloadName = json.document.downloadName
        } else {
          downloadName = `Fichier${Math.floor(Date.now() / 1000)}.pdf`
        }

        await this.downloadFile(`documents/${json.id}/download`, downloadName)
      })
      e.stopPropagation()
      e.preventDefault()
    },
    setDocument({ doc, link }) {
      this.documentToSend = doc
      this.url = link
    },
    async addDocument() {
      if (this.loading) {
        return
      }

      if (Object.entries(this.documentToSend).length <= 0) {
        this.$refs.missing.textContent = this.$t('contract.document.fields.missing.document')
        return
      }

      let documentToAdd = {
        comment: this.comment ? this.comment : '',
        document: this.documentToSend,
      }

      if (this.document.id) {
        documentToAdd['document']['typeId'] = this.document.id
      }

      if (this.internalname !== 'none') {
        documentToAdd['document']['typeId'] = this.internalname
      }

      if (!documentToAdd['document']['typeId']) {
        this.$refs.missing.textContent = this.$t('contract.document.fields.missing.document_type')
        return
      }

      try {
        this.loading = true
        const json = await this.postJSON(this.url, documentToAdd)

        this.$refs.documentAdd.setUploadOk(
          this.document,
          createDocument(json, this.$root).defineDocument(this.document.index),
        )
        this.toast(this.$t('document.uploadingOk', { fileName: this.documentToSend.imageFile.filename }))
        this.closeModal(false)
        this.$refs.documentAdd.removeDocuments()
        this.$emit('update:document')
      } catch (reason) {
        this.$refs.documentAdd.setUploadError(this.document, reason)
      } finally {
        this.loading = false
      }
    },
    async onUpload() {
      if (this.requireConfirmation) {
        this.$refs.confirm.showModal()
      } else {
        await this.addDocument()
      }
    },
    async onGenerate() {
      if (this.requireConfirmation) {
        this.$refs.confirm.showModal()
      } else {
        await this.generateTemplate()
      }
    },
    async onConfirmation() {
      this.$refs.confirm.closeModal()
      if (this.mode === 'upload') {
        await this.addDocument()
      } else {
        await this.generateTemplate()
      }
    },
    selectDocumentTypeById(newId) {
      this.documentType = this.documentTypes.find(({ id }) => id === newId) ?? null
    },
  },
  setup() {
    const { requireConfirmation, confirmationTitle, confirmationMessage, documentType, mode } = useDocumentTypeUnicity()

    return {
      requireConfirmation,
      confirmationTitle,
      confirmationMessage,
      documentType,
      mode,
    }
  },
  beforeMount() {
    if (this.hasDocumentAdd) {
      let contextComplet = this.context
      if (this.context.indexOf('\\') === -1 || !this.context.includes('AppBundle\\Entity\\')) {
        contextComplet = 'AppBundle\\Entity\\' + this.context
      }
      this.getJSON('documents/def?context=' + contextComplet + '&contextId=' + this.contextId).then(
        (json) => {
          this.documentTypeDef = json.config.documentType
          this.commentDef = json.config.comment

          if (this.internalname === 'none') {
            this.documentTypeGenerate = JSON.parse(JSON.stringify(json.config.documentType))
            this.documentTypeGenerate.options.choices = []
            // Conserve les types de document qui n'ont pas de template
            if (this.documentTypeDef.options.choices != null) {
              for (let i = 0; i < this.documentTypeDef.options.choices.length; i++) {
                if (json.config.documentType.options.choices[i].hasTemplate) {
                  this.documentTypeGenerate.options.choices.push(json.config.documentType.options.choices[i])
                }
              }
            }
          } else {
            if (this.documentTypeDef.options.choices.length > 0) {
              this.documentTypeDef.options.choices = this.documentTypeDef.options.choices.filter(
                (documentType) => documentType.internalName === this.internalname,
              )
            }
          }

          this.document = createDocument(json.config, this.$root)
          this.documentGenerate = createDocument(json.config, this.$root)
        },
        (reason) => {
          alert(reason)
        },
      )
    }
  },
  watch: {
    'document.id': {
      handler(newId) {
        this.selectDocumentTypeById(newId)
      },
    },
    'documentGenerate.id': {
      handler(newId) {
        this.selectDocumentTypeById(newId)
      },
    },
  },
}
</script>
<template>
  <div>
    <button
      v-if="internalname !== 'none'"
      :disabled="disabled"
      @click="downloadDocument($event)"
      class="btn btn-sm btn-square btn-outline btn-round btn-primary mx-1"
      style="width: max-content; height: max-content"
      e2e-button="downloadDocument"
    >
      <i class="fa fa-download"></i> {{ $t('documentEntity.buttonDownload') }}
    </button>
    <button
      v-if="hasDocumentAdd"
      type="button"
      :id="id + '-add-document'"
      data-provide="tooltip"
      data-placement="right"
      :data-original-title="$t('documentEntity.buttonAddToolTip')"
      :disabled="disabled"
      @click="openModal(false)"
      class="btn btn-sm btn-square btn-outline btn-round btn-primary mx-1"
      style="width: max-content; height: max-content"
      e2e-button="addDocument"
    >
      <i class="fa fa-lg fa-plus"></i>
      {{ internalname !== 'none' ? $t('documentEntity.buttonImport') : $t('documentEntity.buttonAdd') }}
    </button>
    <button
      v-if="hasDocumentGenerate"
      type="button"
      :id="id + '-generate-document'"
      data-provide="tooltip"
      data-placement="right"
      :data-original-title="$t('documentEntity.buttonGenerateToolTip')"
      :disabled="disabled"
      @click="openModal(true)"
      class="btn btn-sm btn-square btn-outline btn-round btn-primary mx-1"
      style="width: max-content; height: max-content"
      e2e-button="generateDocument"
    >
      <i class="fa fa-lg fa-cog"></i> {{ $t('documentEntity.buttonGenerate') }}
    </button>
    <ModalDialog
      :id-modal-page="id"
      :ref="id + '-modal-add-document'"
      class-modal-dialog="modal-lg"
      :id="id + '-modal-add-document'"
      :title="$t('documentEntity.titleAdd')"
      @icon-close="closeModal"
      e2e-modal="modal-add-document"
    >
      <template #body>
        <SimpleGroupSelect
          v-if="documentTypeDef"
          reference="addDocument"
          :config-options="documentTypes"
          v-model.number="document.id"
          id="documentType"
        />
        <div class="form-group row">
          <label for="add-document" class="col-md-6 col-lg-4 col-xs-12 col-form-label text-right require">
            {{ $t('documentEntity.fields.imageFile.label') }}
          </label>
          <div class="col-md-6 col-lg-6 col-xs-12 require">
            <div class="input-group">
              <DocumentAdd
                ref="documentAdd"
                :context="context"
                :context-id="String(contextId)"
                :document="document"
                @document-uploading="$emit('document-uploading', $event)"
                @document-uploaded="$emit('document-uploaded', $event)"
                @document-uploading-error="$emit('document-uploading-error', $event)"
                @document-to-send="setDocument"
              />
            </div>
          </div>
        </div>
        <label for="comment" class="col-md-6 col-lg-4 col-xs-12 col-form-label">{{ $t('comment.title') }}</label>
        <textarea
          v-if="Object.keys(commentDef).length > 0"
          class="form-control"
          v-model="comment"
          name="comment"
          rows="3"
          cols="50"
          :required="commentDef.options.required"
          :disabled="commentDef.options.disabled"
          e2e-input="documentComment"
        >
        </textarea>
        <div class="row d-flex justify-content-center mt-1">
          <span ref="missing" style="color: red"></span>
        </div>
      </template>
      <template #footer>
        <ButtonAll
          margin="no"
          data-dismiss="modal"
          type="information"
          label="button.cancel"
          @click="closeModal"
          name="close"
        />
        <ButtonAll
          margin="no"
          :type="hasDocumentTypes ? 'validation' : 'disabled'"
          @click="onUpload"
          :loading="loading"
          label="button.validate"
          name="create"
        />
      </template>
    </ModalDialog>
    <ModalDialog
      :id="id + '-modal-generate-document'"
      :id-modal-page="String(id)"
      :ref="id + '-modal-generate-document'"
      class-modal-dialog="modal-lg"
      :title="$t('documentEntity.titleGenerate')"
      @icon-close="closeModal(true)"
      e2e-modal="modal-generate-document"
    >
      <template #body>
        <SimpleGroupSelect
          v-if="hasDocumentTypeGenerates"
          reference="updateDocument"
          v-model.number="documentGenerate.id"
          :config-options="documentTypeGenerate?.options?.choices ?? []"
          id="documentType"
        />
      </template>
      <template #footer>
        <ButtonAll
          margin="no"
          data-dismiss="modal"
          type="information"
          label="button.cancel"
          @click="closeModal(true)"
          name="close"
        />
        <ButtonAll
          margin="no"
          :type="hasDocumentTypeGenerates ? 'validation' : 'disabled'"
          icon="generate"
          label="documentEntity.buttonGeneratePopin"
          @click="onGenerate"
          :loading="generating"
          name="generate"
        />
      </template>
    </ModalDialog>
  </div>
  <template v-if="requireConfirmation">
    <Teleport to="body">
      <ModalConfirmation
        id="cancelDocumentConfirm"
        ref="confirm"
        :title="confirmationMessage"
        :message="confirmationTitle"
        style="z-index: 2000"
        @confirm="onConfirmation"
      />
    </Teleport>
  </template>
</template>
